import { addFakePrincipalHeader } from '@/mocking/fakePrincipal'
import { isProdMode } from '@utils/env.ts'
import { NoConnectionError, NotAuthenticatedError, type ProblemDetails, ProblemDetailsError } from '@utils/errors'

export type RequestData = Record<string, unknown>
export type UrlParam = string | URL

// Generic HTTP Client interface
export interface HttpClient {
  get: <T>(url: UrlParam) => Promise<T>
  delete: (url: UrlParam) => Promise<void>
  post: <T>(url: UrlParam, data: RequestData) => Promise<T>
  put: <T>(url: UrlParam, data: RequestData) => Promise<T>
}

interface FetchConfig {
    timeout?: number
}

interface RequestBase {
    url: string | URL
    method: 'GET' | 'PUT' | 'POST' | 'DELETE' | 'PATCH'
    fetchConfig?: FetchConfig
}

interface GetRequest extends RequestBase {
    method: 'GET'
}

interface DeleteRequest extends RequestBase {
    method: 'DELETE'
}

interface RequestWithBody extends RequestBase {
    method: 'POST' | 'PUT' | 'PATCH'
    data: RequestData
}

type Request = RequestWithBody | GetRequest | DeleteRequest

class BaseHttpClient implements HttpClient {
  private readonly defaultHeaders = new Headers({
    'Accept': 'application/json, application/problem+json',
    'Content-Type': 'application/json',
  })

  // Use arrow functions to match the interface and support safe destructuring
  public get = async <T>(url: UrlParam): Promise<T> => {
    return await this._execute<T>({ method: 'GET', url })
  }

  public delete = async (url: UrlParam): Promise<void> => {
    await this._execute({ method: 'DELETE', url })
  }

  public post = async <T>(url: UrlParam, data?: RequestData): Promise<T> => {
    return await this._execute<T>({ method: 'POST', url, data: data ?? {} })
  }

  public put = async <T>(url: UrlParam, data: RequestData): Promise<T> => {
    return await this._execute<T>({ method: 'PUT', url, data })
  }

  private async _execute(request: DeleteRequest): Promise<void>;
  private async _execute<T>(request: GetRequest | RequestWithBody): Promise<T>;
  private async _execute<T>(request: Request): Promise<T | void> {
    let { url } = request
    const { method } = request

    if (url instanceof URL) {
      url = url.toString()
    }

    let body = undefined
    if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
      body = JSON.stringify(request.data)
    }

    let headers = new Headers(this.defaultHeaders)
    if (!isProdMode) {
      headers = addFakePrincipalHeader(headers)
    }

    let response: Response
    try {
      response = await fetch(url, { method, headers, body })
    } catch {
      // the only time `fetch` throws is when the network is compromised
      throw new NoConnectionError()
    }

    if (!response.ok) {
      if (response.status === 401){
        throw new NotAuthenticatedError()
      }

      if (response.headers.get('Content-Type') !== 'application/problem+json') {
        throw new Error(`Unexpected response status: ${response.status} ${response.statusText}`)
      }

      const problem = await response.json() as ProblemDetails
      throw new ProblemDetailsError(problem)
    }

    if (method === 'DELETE') {
      return
    }

    // 204 responses have no body to be parsed
    if (response.status === 204) return

    return await response.json() as Promise<T>
  }
}

// Export singleton instance
const httpClient = new BaseHttpClient()
export default httpClient
